/**
 * Cross-Reference Heatmap (doc 147 §6.6 E5 + PR Q1 stratify/sort/drill).
 *
 * N×N SVG heatmap of undirected reference counts per entity pair. PR Q1
 * adds:
 *  - Stratification picker (All / FK / Embed / Enum)
 *  - LegendBar (continuous + discrete edge-type swatches)
 *  - DrillDownPanel on cell click revealing the underlying refs
 *  - Sort toggle (alphabetical vs reference-density)
 */
import { useMemo, useState } from 'react';
import type { AtlasManifest, ReferenceKind } from '@atlas-manifest/types';
import {
  buildStratifiedHeatmap,
  sortHeatmap,
  cellReferences,
} from '../state/heatmap.js';
import { useFocusScroll } from '../state/useFocusScroll.js';
import { LegendBar } from '../components/LegendBar.js';
import { DrillDownPanel } from '../components/DrillDownPanel.js';
import { EntityChip } from '../components/EntityChip.js';
import { Chip } from '../components/Chip.js';

export interface HeatmapViewProps {
  manifest: AtlasManifest;
  onNavigateEntity: (entityId: string) => void;
  /** Optional entity id to scroll to + highlight (row label). */
  focus?: string;
}

// Only the kinds users typically slice by — keeps the chip strip
// readable. Other kinds (aggregate/event) still count in the "all" view.
type StratifyValue = 'all' | 'fk' | 'embed' | 'enum';
type SortValue = 'name' | 'rowTotal';

const STRATIFY_LABELS: Record<StratifyValue, string> = {
  all: 'All references',
  fk: 'FK only',
  embed: 'Embed only',
  enum: 'Enum only',
};

function asRefKind(v: StratifyValue): ReferenceKind | undefined {
  return v === 'all' ? undefined : (v as ReferenceKind);
}

const CELL = 14;
const MARGIN = 160;

export function HeatmapView({
  manifest,
  onNavigateEntity,
  focus,
}: HeatmapViewProps): React.JSX.Element {
  const [stratify, setStratify] = useState<StratifyValue>('all');
  const [sortBy, setSortBy] = useState<SortValue>('name');
  const [drillCell, setDrillCell] = useState<{ i: number; j: number } | null>(null);

  const base = useMemo(() => {
    const kind = asRefKind(stratify);
    return buildStratifiedHeatmap(manifest, kind ? { refKind: kind } : {});
  }, [manifest, stratify]);
  const matrix = useMemo(
    () => sortHeatmap(base, { by: sortBy, dir: sortBy === 'rowTotal' ? 'desc' : 'asc' }),
    [base, sortBy],
  );
  useFocusScroll(focus);

  const n = matrix.entityIds.length;
  const size = MARGIN + n * CELL;
  const drillRefs = useMemo(
    () => (drillCell ? cellReferences(matrix, manifest, drillCell.i, drillCell.j) : []),
    [matrix, manifest, drillCell],
  );

  return (
    <div className="heatmap-view">
      <h2>Cross-Reference Heatmap</h2>
      <p className="subtitle">
        Cell intensity = number of references between the two entities.
        Click a cell to inspect the underlying refs.
      </p>

      <div className="heatmap-controls" role="group" aria-label="Heatmap controls">
        <div className="heatmap-control">
          <span className="heatmap-control-label">REFERENCE TYPE</span>
          <div className="heatmap-chipgroup">
            {(Object.keys(STRATIFY_LABELS) as StratifyValue[]).map((v) => (
              <Chip
                key={v}
                label={STRATIFY_LABELS[v]}
                selected={stratify === v}
                onClick={() => setStratify(v)}
              />
            ))}
          </div>
        </div>
        <div className="heatmap-control">
          <span className="heatmap-control-label">SORT</span>
          <div className="heatmap-chipgroup">
            <Chip label="Alphabetical" selected={sortBy === 'name'} onClick={() => setSortBy('name')} />
            <Chip
              label="Reference density"
              selected={sortBy === 'rowTotal'}
              onClick={() => setSortBy('rowTotal')}
            />
          </div>
        </div>
        <div className="heatmap-legend">
          <LegendBar
            mode="continuous"
            min={0}
            max={Math.max(1, matrix.maxCell)}
            ticks={[0, Math.ceil(matrix.maxCell / 4), Math.ceil(matrix.maxCell / 2), Math.ceil((matrix.maxCell * 3) / 4), matrix.maxCell]}
            ariaLabel={`Reference count scale 0 to ${matrix.maxCell}`}
          />
        </div>
      </div>

      <div className="heatmap-stage">
        <div className="heatmap-scroll">
          <svg
            viewBox={`0 0 ${size} ${size}`}
            width={size}
            height={size}
            aria-label="Cross-reference heatmap"
          >
            {/* Row labels */}
            {matrix.entityIds.map((id, i) => (
              <text
                key={`r-${id}`}
                x={MARGIN - 6}
                y={MARGIN + i * CELL + CELL / 2 + 4}
                textAnchor="end"
                className="heatmap-label"
                data-entity-id={id}
              >
                {id}
              </text>
            ))}
            {/* Col labels (rotated) */}
            {matrix.entityIds.map((id, j) => (
              <text
                key={`c-${id}`}
                x={MARGIN + j * CELL + CELL / 2}
                y={MARGIN - 6}
                transform={`rotate(-60 ${MARGIN + j * CELL + CELL / 2} ${MARGIN - 6})`}
                textAnchor="start"
                className="heatmap-label"
              >
                {id}
              </text>
            ))}
            {/* Cells */}
            {matrix.cells.map((row, i) =>
              row.map((value, j) => {
                if (value === 0) return null;
                const intensity = matrix.maxCell > 0 ? value / matrix.maxCell : 0;
                const isSelected =
                  drillCell !== null && drillCell.i === i && drillCell.j === j;
                return (
                  <rect
                    key={`${i}-${j}`}
                    x={MARGIN + j * CELL}
                    y={MARGIN + i * CELL}
                    width={CELL - 1}
                    height={CELL - 1}
                    fill={`hsl(270, 70%, ${85 - intensity * 50}%)`}
                    stroke={isSelected ? 'var(--brand)' : 'none'}
                    strokeWidth={isSelected ? 2 : 0}
                    className="heatmap-cell"
                    onClick={() => setDrillCell({ i, j })}
                    cursor="pointer"
                  >
                    <title>{`${matrix.entityIds[i]} ↔ ${matrix.entityIds[j]}: ${value} ref${value === 1 ? '' : 's'}`}</title>
                  </rect>
                );
              }),
            )}
          </svg>
        </div>

        <DrillDownPanel
          open={drillCell !== null}
          title={
            drillCell
              ? `${matrix.entityIds[drillCell.i]} ↔ ${matrix.entityIds[drillCell.j]}`
              : ''
          }
          subtitle={drillRefs.length === 1 ? '1 reference' : `${drillRefs.length} references`}
          onClose={() => setDrillCell(null)}
        >
          {drillCell && (
            <div className="heatmap-drill-body">
              {drillRefs.length === 0 ? (
                <p className="heatmap-drill-empty">No references for this pair.</p>
              ) : (
                <ul className="heatmap-drill-list">
                  {drillRefs.map((r, i) => (
                    <li key={i} className="heatmap-drill-row">
                      <span className="chip">{r.kind}</span>
                      <span className="mono">
                        {r.from}.{r.fromField}
                      </span>
                      <span className="heatmap-drill-arrow">→</span>
                      <span className="mono">{r.to}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="heatmap-drill-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => onNavigateEntity(matrix.entityIds[drillCell.i]!)}
                >
                  Open {matrix.entityIds[drillCell.i]}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => onNavigateEntity(matrix.entityIds[drillCell.j]!)}
                >
                  Open {matrix.entityIds[drillCell.j]}
                </button>
              </div>
              <div className="heatmap-drill-entities">
                <EntityChip
                  entityId={matrix.entityIds[drillCell.i]!}
                  manifest={manifest}
                />
                <EntityChip
                  entityId={matrix.entityIds[drillCell.j]!}
                  manifest={manifest}
                />
              </div>
            </div>
          )}
        </DrillDownPanel>
      </div>
    </div>
  );
}
